import React, { useState } from 'react';
import { getAdminUserHref } from '@/modules/admin/client/utils/member-url';
import { useTranslation } from 'react-i18next';
import RemoveContact from '@/modules/contacts/client/components/RemoveContactContainer';
import { getProfileExperiencesPath } from '@/modules/core/shared/navigation-links';
import type { ContactRecord } from '@/modules/contacts/client/types';

interface TopNavigationSmallProps {
  isAdmin?: boolean;
  username: string;
  contact?: ContactRecord | null;
  referencesEnabled: boolean;
  isResolved: boolean;
  selfId: string;
  userId: string;
  onContactRemoved: (contact: ContactRecord) => void;
}

interface NavigationLink {
  id: string;
  label: string;
  link?: string;
  onClick?: () => void;
}

export default function TopNavigationSmall({
  isAdmin = false,
  username,
  contact,
  referencesEnabled,
  isResolved,
  selfId,
  userId,
  onContactRemoved,
}: TopNavigationSmallProps) {
  // @TODO this hacky fix should be removed and the data should be consistent
  if (contact) {
    if (typeof contact.userFrom === 'object') {
      contact.userFrom = contact.userFrom._id;
    }
    if (typeof contact.userTo === 'object') {
      contact.userTo = contact.userTo._id;
    }
  }

  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const { t } = useTranslation('users') as {
    t: (key: string, options?: Record<string, unknown>) => string;
  };

  const isSelf = selfId === userId;

  let links: NavigationLink[];
  if (isSelf) {
    links = [
      {
        id: 'edit-profile',
        label: t('Edit your profile'),
        link: '/profile/edit',
      },
    ];
  } else {
    links = [
      {
        id: 'send-message',
        label: t('Send a message'),
        link: `/messages/${username}`,
      },
    ];

    if (referencesEnabled) {
      links.push({
        id: 'share-experience',
        label: t('Share your experience'),
        link: getProfileExperiencesPath(username),
      });
    }

    if (isResolved && contact) {
      const contactLink = !contact._id
        ? {
            id: 'add-contact',
            label: t('Add contact'),
            link: `/contact-add/${userId}`,
          }
        : contact.confirmed
          ? {
              id: 'remove-contact',
              label: t('Remove contact'),
              onClick: () => setShowRemoveModal(true),
            }
          : {
              id: 'delete-contact-request',
              label: t('Delete contact request'),
              onClick: () => setShowRemoveModal(true),
            };

      links.push(contactLink);
    }
  }

  if (isAdmin && userId) {
    links.push({
      id: 'admin',
      label: 'Admin',
      link: getAdminUserHref({ _id: userId, username }),
    });
  }

  function handleRemoveSuccess() {
    setShowRemoveModal(false);

    // Notify the parent about the removal.
    onContactRemoved(contact as ContactRecord);
  }

  return (
    <>
      {contact?._id && (
        <RemoveContact
          selfId={selfId}
          contact={contact as ContactRecord & { _id: string }}
          show={showRemoveModal}
          onSuccess={handleRemoveSuccess}
          onCancel={() => setShowRemoveModal(false)}
        />
      )}
      <nav className="navbar navbar-white navbar-fixed-top navbar-fixed-top-below visible-xs-block">
        <div className="container">
          <ul className="nav navbar-nav" role="navigation">
            {links.map(({ id, label, link, onClick }) => (
              <li key={id}>
                <a href={link} onClick={onClick}>
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </>
  );
}
